import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { EXTERNAL_LINKS } from "../../shared/config/external-links";
import { acceptInvite, storeSession } from "../../shared/api/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

type Rule = { id: string; label: string; test: (p: string) => boolean };

const RULES: Rule[] = [
  { id: "len", label: "≥ 8 ký tự", test: (p) => p.length >= 8 },
  { id: "letter", label: "Có chữ cái", test: (p) => /[A-Za-zÀ-ỹ]/.test(p) },
  { id: "digit", label: "Có chữ số", test: (p) => /\d/.test(p) },
  { id: "symbol", label: "Có ký tự đặc biệt", test: (p) => /[^A-Za-z0-9À-ỹ]/.test(p) },
];

function strengthOf(p: string): { score: 0 | 1 | 2 | 3 | 4; label: string; key: string } {
  const passed = RULES.filter((r) => r.test(p)).length;
  if (p.length === 0) return { score: 0, label: "", key: "" };
  if (passed <= 1) return { score: 1, label: "Yếu", key: "weak" };
  if (passed === 2) return { score: 2, label: "Trung bình", key: "fair" };
  if (passed === 3) return { score: 3, label: "Khá", key: "good" };
  return { score: 4, label: "Mạnh", key: "strong" };
}

const STRENGTH_BAR: Record<string, string> = {
  weak: "bg-destructive",
  fair: "bg-amber-500",
  good: "bg-yellow-500",
  strong: "bg-emerald-500",
};

function BrandHeader() {
  return (
    <div className="mb-6 text-center">
      <div className="inline-flex items-center gap-2 text-3xl font-extrabold tracking-tight text-primary">
        <span aria-hidden="true">✚</span> Addy HIS
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Hệ thống quản lý phòng khám</p>
    </div>
  );
}

export function AcceptInvitePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strength = useMemo(() => strengthOf(password), [password]);

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4 font-sans">
        <div className="w-full max-w-md">
          <BrandHeader />
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Lời mời không hợp lệ</CardTitle>
              <CardDescription>Link mời không có token hoặc đã hết hạn.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                Liên hệ chủ phòng khám để được gửi lại lời mời mới.
              </div>
              <Button asChild className="w-full">
                <Link to="/login">Quay lại đăng nhập</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!fullName.trim()) {
      setError("Vui lòng nhập họ tên đầy đủ.");
      return;
    }
    if (strength.score < 3) {
      setError("Mật khẩu chưa đủ mạnh.");
      return;
    }
    if (password !== confirm) {
      setError("Hai mật khẩu không khớp.");
      return;
    }
    if (!accept) {
      setError("Vui lòng đồng ý các điều khoản trước khi tiếp tục.");
      return;
    }
    setSubmitting(true);
    try {
      const tokens = await acceptInvite(token!, fullName.trim(), password);
      storeSession(tokens);
      // Mandatory MFA setup after accepting an invite.
      navigate("/mfa-setup");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể tạo tài khoản.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4 font-sans">
      <div className="w-full max-w-md">
        <BrandHeader />

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Bạn được mời tham gia phòng khám</CardTitle>
            <CardDescription>Hoàn tất hồ sơ để bắt đầu sử dụng Addy HIS.</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={onSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="fullName">Họ và tên</Label>
                <Input
                  id="fullName"
                  type="text"
                  autoComplete="name"
                  autoFocus
                  placeholder="Nguyễn Thị Minh"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mật khẩu</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
                {password.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex gap-1.5">
                      {[1, 2, 3, 4].map((i) => (
                        <span
                          key={i}
                          className={`h-1.5 flex-1 rounded-full ${
                            strength.score >= i ? STRENGTH_BAR[strength.key] : "bg-muted"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      Độ mạnh: <strong className="text-foreground">{strength.label}</strong>
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm">Xác nhận mật khẩu</Label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  className="mt-0.5"
                  checked={accept}
                  onCheckedChange={(v) => setAccept(v === true)}
                  disabled={submitting}
                />
                <span>
                  Tôi đồng ý với{" "}
                  <a
                    href={EXTERNAL_LINKS.terms}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary hover:underline"
                  >
                    Điều khoản dịch vụ
                  </a>{" "}
                  và{" "}
                  <a
                    href={EXTERNAL_LINKS.privacy}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary hover:underline"
                  >
                    Chính sách bảo mật
                  </a>
                  .
                </span>
              </label>

              {error && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Đang tạo tài khoản…" : "Tiếp tục thiết lập MFA"}
              </Button>

              <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
                <Lock className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                <div>
                  Sau khi tạo tài khoản, bạn sẽ được yêu cầu thiết lập xác thực 2 lớp ngay — yêu cầu
                  bắt buộc cho mọi nhân viên truy cập dữ liệu bệnh nhân.
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
